import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CANVAS_CONFIG, type PaletteColor, type PixelMeta, type Comment } from '@the-wall/shared';
import { api } from '@/services/api';
import { setCommentNewHandler } from '@/services/socket';
import { useAuthStore } from '@/stores/authStore';
import { showSnackbar } from '@/stores/snackbarStore';
import { ConfirmModal } from '@/components/ConfirmModal';
import { UserAvatar } from '@/components/UserAvatar';
import { UserBadges } from '@/components/UserBadges';
import { UsernameText } from '@/components/UsernameText';
import {
  computeZoomAtPoint,
  getCursorPositionFromWheelEvent,
  getWheelZoomDelta,
} from '@/utils/canvasZoom';
import { TimelineScrubber } from './TimelineScrubber';

const PIXEL_SIZE = 1;
const MIN_VISIBLE_PERCENT = 0.75; // Keep at least 75% of canvas visible
const MIN_VIEWPORT_FILL = 0.8; // Canvas should fill at least 80% of viewport at min zoom

// Calculate minimum zoom to fill at least 80% of viewport
function calculateMinZoom(
  containerWidth: number,
  containerHeight: number,
  canvasPixelWidth: number,
  canvasPixelHeight: number
): number {
  const canvasWidth = canvasPixelWidth * PIXEL_SIZE;
  const canvasHeight = canvasPixelHeight * PIXEL_SIZE;

  const zoomForWidth = (containerWidth * MIN_VIEWPORT_FILL) / canvasWidth;
  const zoomForHeight = (containerHeight * MIN_VIEWPORT_FILL) / canvasHeight;

  return Math.min(zoomForWidth, zoomForHeight);
}

// Calculate max pan distance to keep at least MIN_VISIBLE_PERCENT of canvas on screen
function calculateMaxPan(canvasSize: number, viewportSize: number, zoom: number): number {
  const scaledCanvasSize = canvasSize * zoom;

  // When zoomed in (canvas bigger than viewport):
  // Keep at least MIN_VISIBLE_PERCENT of viewport filled with canvas
  const zoomedInLimit = (scaledCanvasSize - viewportSize * MIN_VISIBLE_PERCENT) / 2;

  // When zoomed out (canvas smaller than viewport):
  // Keep at least MIN_VISIBLE_PERCENT of canvas visible
  const zoomedOutLimit = ((1 - MIN_VISIBLE_PERCENT) * scaledCanvasSize) / 2;

  // Use whichever gives more freedom (handles transition smoothly)
  return Math.max(0, Math.max(zoomedInLimit, zoomedOutLimit));
}

// Format the time ago for a pixel on the format "2026-02-05 12:00:00 (Timezone)"
function formatPixelTimeAgo(dateString: string): string {
  // SQLite datetime('now') stores UTC without 'Z' suffix, so JS interprets as local time
  // Append 'Z' if not present to ensure UTC parsing
  const normalizedDate = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalizedDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
  });
}

function formatTimeAgo(dateString: string): string {
  // SQLite datetime('now') stores UTC without 'Z' suffix, so JS interprets as local time
  // Append 'Z' if not present to ensure UTC parsing
  const normalizedDate = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalizedDate);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

// Format time as HH:MM:SS AM/PM for compact mobile display
function formatCompactTime(dateString: string): string {
  const normalizedDate = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalizedDate);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

interface ArchiveCommentItemProps {
  comment: Comment;
  currentUserId: string | undefined;
  currentUserRole: string | null | undefined;
  deletingCommentId: string | null;
  votingCommentId: string | null;
  onDelete: (commentId: string) => void;
  onVote: (commentId: string, vote: 1 | -1) => void;
}

function ArchiveCommentItem({
  comment,
  currentUserId,
  currentUserRole,
  deletingCommentId,
  votingCommentId,
  onDelete,
  onVote,
}: ArchiveCommentItemProps) {
  const isOwnComment = currentUserId === comment.userId;
  const isAdmin = currentUserRole === 'ADMIN' || currentUserRole === 'SUPER_ADMIN';
  const canDelete = isOwnComment || isAdmin;
  const isDeleting = deletingCommentId === comment.id;

  return (
    <div className="flex gap-3 group">
      {comment.profilePicture ? (
        <img
          src={comment.profilePicture}
          alt={comment.username}
          className="w-8 h-8 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-linear-to-br from-wall-accent to-wall-accent-secondary flex items-center justify-center text-xs font-bold text-white shrink-0">
          {comment.username.slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <UsernameText
            text={comment.username}
            color={comment.usernameColor ?? null}
            effect={comment.usernameEffect ?? null}
            className="font-medium text-sm"
          />
          <UserBadges userId={comment.userId} className="shrink-0 translate-y-0.5" />
          <span className="text-xs text-wall-muted">{formatTimeAgo(comment.createdAt)}</span>
          {canDelete && (
            <button
              onClick={() => onDelete(comment.id)}
              disabled={isDeleting}
              className="ml-auto opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 active:opacity-100 transition-opacity text-wall-muted hover:text-red-400 disabled:opacity-50 p-1 -m-1"
              title="Delete comment"
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
        <p className="text-sm text-wall-text/90 mt-0.5 wrap-break-word">{comment.content}</p>
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            onClick={() => onVote(comment.id, 1)}
            disabled={votingCommentId === comment.id}
            className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 transition-colors disabled:opacity-50 ${
              comment.userVote === 1
                ? 'text-emerald-400 bg-emerald-500/20'
                : 'text-wall-muted hover:text-wall-text hover:bg-wall-border/30'
            }`}
            title="Thumbs up"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 11H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3" />
            </svg>
            <span>{comment.upvotes ?? 0}</span>
          </button>
          <button
            type="button"
            onClick={() => onVote(comment.id, -1)}
            disabled={votingCommentId === comment.id}
            className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 transition-colors disabled:opacity-50 ${
              comment.userVote === -1
                ? 'text-red-400 bg-red-500/20'
                : 'text-wall-muted hover:text-wall-text hover:bg-wall-border/30'
            }`}
            title="Thumbs down"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-3" />
            </svg>
            <span>{comment.downvotes ?? 0}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function ArchiveDetail() {
  const { id } = useParams<{ id: string }>();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveDate, setArchiveDate] = useState<string>('');
  const [pixels, setPixels] = useState<(PaletteColor | null)[][]>([]);
  const [pixelMeta, setPixelMeta] = useState<Record<string, PixelMeta>>({});
  const [canvasWidth, setCanvasWidth] = useState<number>(CANVAS_CONFIG.WIDTH);
  const [canvasHeight, setCanvasHeight] = useState<number>(CANVAS_CONFIG.HEIGHT);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [votingCommentId, setVotingCommentId] = useState<string | null>(null);
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false); // mobile: comments drawer open

  const COMMENT_DEBOUNCE_MS = 2000; // 2 seconds between comments

  // Viewport state
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.5);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [hoveredPixel, setHoveredPixel] = useState<{ x: number; y: number } | null>(null);
  const [selectedPixel, setSelectedPixel] = useState<{ x: number; y: number } | null>(null);
  const [canvasFocused, setCanvasFocused] = useState(false);

  // Timeline scrubber state
  const [timelineTime, setTimelineTime] = useState<number | null>(null);

  // Pre-compute pixel timestamps for timeline filtering
  const pixelTimeMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const [key, meta] of Object.entries(pixelMeta)) {
      const normalized = meta.placedAt.endsWith('Z')
        ? meta.placedAt
        : meta.placedAt.replace(' ', 'T') + 'Z';
      map[key] = new Date(normalized).getTime();
    }
    return map;
  }, [pixelMeta]);

  const sortedTimestamps = useMemo(
    () => Object.values(pixelTimeMap).sort((a, b) => a - b),
    [pixelTimeMap]
  );

  const timelineMin = sortedTimestamps.length > 0 ? sortedTimestamps[0]! : 0;
  const timelineMax =
    sortedTimestamps.length > 0 ? sortedTimestamps[sortedTimestamps.length - 1]! : 0;

  const handleTimelineChange = useCallback((time: number) => {
    setTimelineTime(time);
  }, []);

  const handleTimelineReset = useCallback(() => {
    setTimelineTime(null);
  }, []);

  // Helper to clamp pan values
  const clampPan = useCallback(
    (x: number, y: number, currentZoom?: number) => {
      const z = currentZoom ?? zoom;
      const vw = viewportWidth || 1;
      const vh = viewportHeight || 1;
      const maxPanX = calculateMaxPan(canvasWidth * PIXEL_SIZE, vw, z);
      const maxPanY = calculateMaxPan(canvasHeight * PIXEL_SIZE, vh, z);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, x)),
        y: Math.max(-maxPanY, Math.min(maxPanY, y)),
      };
    },
    [canvasHeight, canvasWidth, zoom, viewportWidth, viewportHeight]
  );

  // Drag state
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartPan = useRef({ x: 0, y: 0 });

  // Touch/pinch state
  const lastPinchDistance = useRef<number | null>(null);
  const lastPinchZoom = useRef<number>(1);
  const pinchCenter = useRef({ x: 0, y: 0 });
  const pinchStartPan = useRef({ x: 0, y: 0 });

  // Refs to track current zoom/pan for event handlers (avoid stale closures)
  const zoomRef = useRef(zoom);
  const panRef = useRef({ x: panX, y: panY });
  const wheelRafScheduledRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    panRef.current = { x: panX, y: panY };
  }, [panX, panY]);

  const DRAG_THRESHOLD = 5;

  // Fetch archive data
  useEffect(() => {
    if (!id) return;

    const archiveId = id;
    setTimelineTime(null); // Reset timeline when changing archives

    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        const [archive, pixelData, commentsData] = await Promise.all([
          api.getArchive(archiveId, token),
          api.getArchivePixels(archiveId, token),
          api.getArchiveComments(archiveId, token),
        ]);

        setArchiveDate(archive.date);
        setPixels(pixelData.pixels);
        setPixelMeta(pixelData.pixelMeta);
        setCanvasWidth(pixelData.width);
        setCanvasHeight(pixelData.height);
        setComments(commentsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load archive');
      } finally {
        setIsLoading(false);
      }
    }

    void fetchData();
  }, [id, token]);

  useEffect(() => {
    if (!user?.id) return;

    setComments((prev) =>
      prev.map((comment) =>
        comment.userId !== user.id
          ? comment
          : {
              ...comment,
              usernameColor: user.usernameColor ?? null,
              usernameEffect: user.usernameEffect ?? null,
            }
      )
    );
  }, [user?.id, user?.usernameColor, user?.usernameEffect]);

  // Live updates: comment:new is handled by the app-level socket connection.
  useEffect(() => {
    if (!id) return;

    const handler = (data: { archiveId: string; comment: Comment }) => {
      if (data.archiveId !== id) return;
      setComments((prev) =>
        prev.some((c) => c.id === data.comment.id) ? prev : [...prev, data.comment]
      );
    };

    setCommentNewHandler(handler);
    return () => {
      setCommentNewHandler(null);
    };
  }, [id]);

  // Calculate initial zoom to fit canvas
  useEffect(() => {
    const container = containerRef.current;
    if (!container || pixels.length === 0) return;

    const { width, height } = container.getBoundingClientRect();
    setViewportWidth(width);
    setViewportHeight(height);

    const canvasPixelWidth = canvasWidth * PIXEL_SIZE;
    const canvasPixelHeight = canvasHeight * PIXEL_SIZE;

    // Calculate min zoom based on viewport
    const calculatedMinZoom = calculateMinZoom(width, height, canvasWidth, canvasHeight);
    setMinZoom(calculatedMinZoom);

    // Initial zoom to fit canvas nicely (90% of viewport)
    const fitZoom = Math.min((width * 0.9) / canvasPixelWidth, (height * 0.9) / canvasPixelHeight);
    setZoom(Math.max(fitZoom, calculatedMinZoom));
  }, [canvasHeight, canvasWidth, pixels]);

  // Track viewport size changes (only when container actually resizes — do not depend on pan/zoom or we clamp after every zoom)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateViewportSize = () => {
      const { width, height } = container.getBoundingClientRect();
      setViewportWidth(width);
      setViewportHeight(height);

      const newMinZoom = calculateMinZoom(width, height, canvasWidth, canvasHeight);
      setMinZoom(newMinZoom);

      setZoom((currentZoom) => Math.max(currentZoom, newMinZoom));

      // Clamp pan using refs so we don't need pan in deps; only clamp on real resize, not after wheel zoom
      const currentZoom = zoomRef.current;
      const { x: px, y: py } = panRef.current;
      const maxPanX = calculateMaxPan(canvasWidth * PIXEL_SIZE, width, currentZoom);
      const maxPanY = calculateMaxPan(canvasHeight * PIXEL_SIZE, height, currentZoom);
      setPanX(Math.max(-maxPanX, Math.min(maxPanX, px)));
      setPanY(Math.max(-maxPanY, Math.min(maxPanY, py)));
    };

    const resizeObserver = new ResizeObserver(updateViewportSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [canvasHeight, canvasWidth]); // Observer callback only runs on actual container resize

  // Render canvas (with optional timeline filtering)
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || pixels.length === 0) return;

    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let y = 0; y < canvasHeight; y++) {
      for (let x = 0; x < canvasWidth; x++) {
        const color = pixels[y]?.[x];
        if (color) {
          // Timeline filter: skip pixels placed after the current timeline position
          if (timelineTime !== null) {
            const pixelTime = pixelTimeMap[`${x},${y}`];
            if (pixelTime !== undefined && pixelTime > timelineTime) continue;
          }
          ctx.fillStyle = color;
          ctx.fillRect(x * PIXEL_SIZE, y * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
        }
      }
    }
  }, [canvasHeight, canvasWidth, pixels, timelineTime, pixelTimeMap]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Get pixel coordinates from mouse event
  const getPixelCoords = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return null;

      const rect = container.getBoundingClientRect();
      const containerCenterX = rect.width / 2;
      const containerCenterY = rect.height / 2;

      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;

      // Offset from container center in screen pixels
      const offsetX = mouseX - containerCenterX - panX;
      const offsetY = mouseY - containerCenterY - panY;

      // Convert directly to grid coordinates
      const x = Math.floor(offsetX / (zoom * PIXEL_SIZE) + canvasWidth / 2);
      const y = Math.floor(offsetY / (zoom * PIXEL_SIZE) + canvasHeight / 2);

      if (x >= 0 && x < canvasWidth && y >= 0 && y < canvasHeight) {
        return { x, y };
      }
      return null;
    },
    [canvasHeight, canvasWidth, panX, panY, zoom]
  );

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      isDragging.current = true;
      hasDragged.current = false;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragStartPan.current = { x: panX, y: panY };
    },
    [panX, panY]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const coords = getPixelCoords(e.clientX, e.clientY);
      setHoveredPixel(coords);

      if (isDragging.current) {
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;

        if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }

        if (hasDragged.current) {
          const clamped = clampPan(
            dragStartPan.current.x + deltaX,
            dragStartPan.current.y + deltaY
          );
          setPanX(clamped.x);
          setPanY(clamped.y);
        }
      }
    },
    [getPixelCoords, clampPan]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current) return;

      if (!hasDragged.current) {
        const coords = getPixelCoords(e.clientX, e.clientY);
        if (coords) {
          setSelectedPixel((prev) =>
            prev && prev.x === coords.x && prev.y === coords.y ? null : coords
          );
        } else {
          setSelectedPixel(null);
        }
      }

      isDragging.current = false;
      hasDragged.current = false;
    },
    [getPixelCoords]
  );

  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
    hasDragged.current = false;
    setHoveredPixel(null);
  }, []);

  // Zoom-at-point — same math as main canvas via utils/canvasZoom; flush to state at most once per frame
  const applyZoomAtPoint = useCallback(
    (newZoom: number, cursorX: number, cursorY: number) => {
      const result = computeZoomAtPoint({
        zoom: zoomRef.current,
        panX: panRef.current.x,
        panY: panRef.current.y,
        pointX: cursorX,
        pointY: cursorY,
        newZoom,
        minZoom,
        maxZoom: CANVAS_CONFIG.MAX_ZOOM,
      });
      if (!result) return;

      zoomRef.current = result.zoom;
      panRef.current = { x: result.panX, y: result.panY };

      if (!wheelRafScheduledRef.current) {
        wheelRafScheduledRef.current = true;
        requestAnimationFrame(() => {
          setZoom(zoomRef.current);
          setPanX(panRef.current.x);
          setPanY(panRef.current.y);
          wheelRafScheduledRef.current = false;
        });
      }
    },
    [minZoom]
  );

  // Wheel zoom: native listener with passive: false so preventDefault is allowed
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const rect = container.getBoundingClientRect();
      const { cursorX, cursorY } = getCursorPositionFromWheelEvent(rect, e.clientX, e.clientY);
      const delta = getWheelZoomDelta(e.deltaY);
      const newZoom = zoomRef.current * delta;

      applyZoomAtPoint(newZoom, cursorX, cursorY);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [applyZoomAtPoint]);

  // Get distance between two touch points
  const getTouchDistance = (touches: React.TouchList) => {
    const t0 = touches[0];
    const t1 = touches[1];
    if (!t0 || !t1) return null;
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Get center point between two touches (relative to container center)
  const getTouchCenter = useCallback((touches: React.TouchList) => {
    const t0 = touches[0];
    const t1 = touches[1];
    const container = containerRef.current;
    if (!t0 || !t1 || !container) return null;
    const rect = container.getBoundingClientRect();
    return {
      x: (t0.clientX + t1.clientX) / 2 - rect.left - rect.width / 2,
      y: (t0.clientY + t1.clientY) / 2 - rect.top - rect.height / 2,
    };
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        // Single finger - start drag (use refs for current values)
        const touch = e.touches[0];
        if (!touch) return;
        isDragging.current = true;
        hasDragged.current = false;
        dragStartPos.current = { x: touch.clientX, y: touch.clientY };
        dragStartPan.current = { x: panRef.current.x, y: panRef.current.y };
      } else if (e.touches.length === 2) {
        // Two fingers - start pinch zoom (use refs for current values)
        isDragging.current = false;
        const dist = getTouchDistance(e.touches);
        if (dist) {
          lastPinchDistance.current = dist;
          lastPinchZoom.current = zoomRef.current;
        }
        const center = getTouchCenter(e.touches);
        if (center) {
          pinchCenter.current = center;
          pinchStartPan.current = { x: panRef.current.x, y: panRef.current.y };
        }
      }
    },
    [getTouchCenter]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging.current) {
        // Single finger drag
        const touch = e.touches[0];
        if (!touch) return;
        const deltaX = touch.clientX - dragStartPos.current.x;
        const deltaY = touch.clientY - dragStartPos.current.y;

        if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }

        if (hasDragged.current) {
          const currentZoom = zoomRef.current;
          const vw = viewportWidth || 1;
          const vh = viewportHeight || 1;
          const maxPanX = calculateMaxPan(canvasWidth * PIXEL_SIZE, vw, currentZoom);
          const maxPanY = calculateMaxPan(canvasHeight * PIXEL_SIZE, vh, currentZoom);
          const newPanX = dragStartPan.current.x + deltaX;
          const newPanY = dragStartPan.current.y + deltaY;
          setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
          setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
        }
      } else if (e.touches.length === 2 && lastPinchDistance.current !== null) {
        // Two finger pinch zoom towards center point between fingers
        const dist = getTouchDistance(e.touches);
        if (dist) {
          const scale = dist / lastPinchDistance.current;
          const newZoom = Math.max(
            minZoom,
            Math.min(CANVAS_CONFIG.MAX_ZOOM, lastPinchZoom.current * scale)
          );

          // If zoom didn't change (at min/max limit), don't update
          if (newZoom === zoomRef.current) {
            return;
          }

          // Calculate canvas point under pinch center (at start of pinch)
          const canvasPointX =
            (pinchCenter.current.x - pinchStartPan.current.x) / lastPinchZoom.current;
          const canvasPointY =
            (pinchCenter.current.y - pinchStartPan.current.y) / lastPinchZoom.current;

          // Calculate new pan to keep the same canvas point under pinch center
          const newPanX = pinchCenter.current.x - canvasPointX * newZoom;
          const newPanY = pinchCenter.current.y - canvasPointY * newZoom;

          // Update refs and state without clamping during zoom
          zoomRef.current = newZoom;
          panRef.current = { x: newPanX, y: newPanY };

          setZoom(newZoom);
          setPanX(newPanX);
          setPanY(newPanY);
        }
      }
    },
    [canvasHeight, canvasWidth, minZoom, viewportHeight, viewportWidth]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 0) {
        // All fingers lifted
        if (!hasDragged.current && isDragging.current) {
          // This was a tap - select pixel
          const touch = e.changedTouches[0];
          if (touch) {
            const coords = getPixelCoords(touch.clientX, touch.clientY);
            if (coords) {
              setSelectedPixel((prev) =>
                prev && prev.x === coords.x && prev.y === coords.y ? null : coords
              );
            } else {
              setSelectedPixel(null);
            }
          }
        }
        isDragging.current = false;
        hasDragged.current = false;
        lastPinchDistance.current = null;
      } else if (e.touches.length === 1) {
        // One finger still down - switch back to drag mode
        const touch = e.touches[0];
        if (touch) {
          isDragging.current = true;
          hasDragged.current = true;
          dragStartPos.current = { x: touch.clientX, y: touch.clientY };
          dragStartPan.current = { x: panX, y: panY };
          lastPinchDistance.current = null;
        }
      }
    },
    [getPixelCoords, panX, panY]
  );

  // Prevent browser zoom gestures
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preventWheelZoom = (e: WheelEvent) => {
      if (container.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const preventGesture = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['+', '-', '=', '0'].includes(e.key)) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', preventWheelZoom, { passive: false, capture: true });
    window.addEventListener('gesturestart', preventGesture, { capture: true });
    window.addEventListener('gesturechange', preventGesture, { capture: true });
    window.addEventListener('gestureend', preventGesture, { capture: true });
    window.addEventListener('keydown', preventKeyboardZoom, { capture: true });

    return () => {
      window.removeEventListener('wheel', preventWheelZoom, { capture: true });
      window.removeEventListener('gesturestart', preventGesture, { capture: true });
      window.removeEventListener('gesturechange', preventGesture, { capture: true });
      window.removeEventListener('gestureend', preventGesture, { capture: true });
      window.removeEventListener('keydown', preventKeyboardZoom, { capture: true });
    };
  }, []);

  // Arrow key panning when canvas is focused
  useEffect(() => {
    if (!canvasFocused) return;

    const PAN_STEP = 50;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      e.preventDefault();

      let newPanX = panX;
      let newPanY = panY;

      switch (e.key) {
        case 'ArrowUp':
          newPanY = panY + PAN_STEP;
          break;
        case 'ArrowDown':
          newPanY = panY - PAN_STEP;
          break;
        case 'ArrowLeft':
          newPanX = panX + PAN_STEP;
          break;
        case 'ArrowRight':
          newPanX = panX - PAN_STEP;
          break;
      }

      const clamped = clampPan(newPanX, newPanY);
      setPanX(clamped.x);
      setPanY(clamped.y);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvasFocused, panX, panY, clampPan]);

  // Submit comment with debounce
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !id || !newComment.trim() || isSubmitting) return;

    // Check debounce
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime;
    if (timeSinceLastSubmit < COMMENT_DEBOUNCE_MS) {
      const remainingSeconds = Math.ceil((COMMENT_DEBOUNCE_MS - timeSinceLastSubmit) / 1000);
      showSnackbar(`Please wait ${remainingSeconds}s before posting again`, 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const comment = await api.createComment(token, id, newComment.trim());
      setComments((prev) =>
        prev.some((entry) => entry.id === comment.id) ? prev : [...prev, comment]
      );
      setNewComment('');
      setLastSubmitTime(Date.now());
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to post comment', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete comment - open confirmation modal
  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
  };

  // Confirm delete comment
  const handleConfirmDelete = async () => {
    if (!token || !commentToDelete || deletingCommentId) return;

    setDeletingCommentId(commentToDelete);
    try {
      await api.deleteComment(token, commentToDelete);
      setComments((prev) => prev.filter((c) => c.id !== commentToDelete));
      showSnackbar('Comment deleted', 'info');
      setCommentToDelete(null);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete comment', 'error');
    } finally {
      setDeletingCommentId(null);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    if (!deletingCommentId) {
      setCommentToDelete(null);
    }
  };

  // Vote on comment (thumbs up/down). Toggle: same vote again removes.
  const handleVote = async (commentId: string, vote: 1 | -1) => {
    if (!token) {
      showSnackbar('Log in to vote on comments', 'warning');
      return;
    }
    setVotingCommentId(commentId);
    try {
      const result = await api.voteComment(token, commentId, vote);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                upvotes: result.upvotes,
                downvotes: result.downvotes,
                userVote: result.userVote,
              }
            : c
        )
      );
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to update vote', 'error');
    } finally {
      setVotingCommentId(null);
    }
  };

  // Get the pixel to display info for (selected takes priority over hovered)
  const displayPixel = selectedPixel ?? hoveredPixel;
  const isPixelTimeVisible = displayPixel
    ? timelineTime === null ||
      (pixelTimeMap[`${displayPixel.x},${displayPixel.y}`] ?? 0) <= timelineTime
    : false;
  const displayMeta =
    displayPixel && isPixelTimeVisible ? pixelMeta[`${displayPixel.x},${displayPixel.y}`] : null;
  const displayColor =
    displayPixel && isPixelTimeVisible ? pixels[displayPixel.y]?.[displayPixel.x] : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-red-400">{error}</p>
        <Link to="/gallery" className="text-wall-accent hover:underline">
          ← Back to Gallery
        </Link>
      </div>
    );
  }

  const canvasStyle = {
    transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
    imageRendering: 'pixelated' as const,
  };

  return (
    <div className="h-full flex flex-col lg:flex-row overflow-hidden">
      {/* Left: Canvas Viewer — min-w-0 so column width is fixed by layout, not by footer content (avoids pushing comments) */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 pb-20 lg:pb-0">
        {/* Header */}
        <div className="shrink-0 px-3 py-2 sm:px-4 sm:py-3 border-b border-wall-border/50 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Link
              to="/gallery"
              className="text-wall-muted hover:text-wall-text transition-colors flex items-center gap-1 shrink-0"
            >
              <svg
                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden sm:inline">Gallery</span>
            </Link>
            <h1 className="text-base sm:text-xl font-bold text-gradient truncate">
              {new Date(archiveDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </h1>
          </div>
          <div className="text-xs sm:text-sm text-wall-muted shrink-0 tabular-nums">
            {Math.round(zoom * 100)}%
          </div>
        </div>

        {/* Canvas Container */}
        <div
          ref={containerRef}
          tabIndex={0}
          className={`relative flex-1 overflow-hidden bg-wall-bg flex items-center justify-center select-none outline-hidden ${
            canvasFocused ? 'ring-2 ring-wall-accent/50 ring-inset' : ''
          }`}
          style={{
            touchAction: 'none',
            overscrollBehavior: 'contain',
            WebkitUserSelect: 'none',
          }}
          onMouseDown={(e) => {
            containerRef.current?.focus();
            handleMouseDown(e);
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onFocus={() => setCanvasFocused(true)}
          onBlur={() => setCanvasFocused(false)}
        >
          <canvas
            ref={canvasRef}
            width={canvasWidth * PIXEL_SIZE}
            height={canvasHeight * PIXEL_SIZE}
            style={canvasStyle}
            className="border border-wall-border"
          />

          {/* Selected pixel indicator */}
          {selectedPixel && (
            <div
              className="absolute pointer-events-none border-2 border-wall-accent animate-pulse"
              style={{
                width: PIXEL_SIZE * zoom,
                height: PIXEL_SIZE * zoom,
                left: `calc(50% + ${panX}px + ${(selectedPixel.x - canvasWidth / 2) * PIXEL_SIZE * zoom}px)`,
                top: `calc(50% + ${panY}px + ${(selectedPixel.y - canvasHeight / 2) * PIXEL_SIZE * zoom}px)`,
              }}
            />
          )}

          {/* Hover indicator */}
          {hoveredPixel &&
            zoom >= 4 &&
            (!selectedPixel ||
              hoveredPixel.x !== selectedPixel.x ||
              hoveredPixel.y !== selectedPixel.y) && (
              <div
                className="absolute pointer-events-none border-2 border-white mix-blend-difference"
                style={{
                  width: PIXEL_SIZE * zoom,
                  height: PIXEL_SIZE * zoom,
                  left: `calc(50% + ${panX}px + ${(hoveredPixel.x - canvasWidth / 2) * PIXEL_SIZE * zoom}px)`,
                  top: `calc(50% + ${panY}px + ${(hoveredPixel.y - canvasHeight / 2) * PIXEL_SIZE * zoom}px)`,
                }}
              />
            )}
        </div>

        {/* Timeline Scrubber */}
        {sortedTimestamps.length > 0 && (
          <TimelineScrubber
            minTime={timelineMin}
            maxTime={timelineMax}
            currentTime={timelineTime}
            onChange={handleTimelineChange}
            onReset={handleTimelineReset}
            pixelTimestamps={sortedTimestamps}
          />
        )}

        {/* Pixel Info Footer — fixed height, single line on desktop; long values truncated with title tooltip */}
        <div className="shrink-0 min-h-[40px] sm:h-14 sm:overflow-hidden flex items-center px-3 py-1.5 sm:px-4 sm:py-3 border-t border-wall-border/50 bg-wall-surface/50">
          {displayPixel ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-6 sm:flex-nowrap sm:min-w-0 sm:w-full">
              {/* Mobile: Compact single line */}
              <div className="flex items-center gap-1.5 sm:hidden flex-wrap">
                {selectedPixel && (
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-wall-accent animate-pulse" />
                    <span className="text-[10px] text-wall-accent font-medium uppercase">Sel</span>
                  </div>
                )}
                <span className="font-mono text-[10px] text-wall-muted">
                  ({displayPixel.x},{displayPixel.y})
                </span>
                {displayColor && (
                  <>
                    <div
                      className="w-3 h-3 rounded-sm border border-wall-border shrink-0"
                      style={{ backgroundColor: displayColor }}
                    />
                    {displayMeta && (
                      <>
                        {displayMeta.userId ? (
                          <UserAvatar
                            userId={displayMeta.userId}
                            username={displayMeta.username}
                            className="h-5 w-5 shrink-0"
                          />
                        ) : (
                          <span className="h-5 w-5 shrink-0 rounded-full overflow-hidden bg-wall-border/50 flex items-center justify-center">
                            <span className="text-[8px] font-medium text-wall-muted leading-none">
                              {displayMeta.username.slice(0, 2).toUpperCase()}
                            </span>
                          </span>
                        )}
                        <span
                          className="text-[10px] text-wall-highlight font-medium truncate max-w-[60px]"
                          title={displayMeta.username}
                        >
                          {displayMeta.username}
                        </span>
                        <span className="text-[9px] text-wall-muted/80 font-mono">
                          {formatCompactTime(displayMeta.placedAt)}
                        </span>
                      </>
                    )}
                  </>
                )}
                {!displayColor && <span className="text-[10px] text-wall-muted italic">Empty</span>}
              </div>
              {/* Desktop: Single line — placer (avatar + name) then Pos, Color, When */}
              <div className="hidden sm:flex items-center gap-6 flex-nowrap min-w-0 overflow-hidden">
                {selectedPixel && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className="w-2 h-2 rounded-full bg-wall-accent animate-pulse" />
                    <span className="text-xs text-wall-accent font-medium uppercase">Selected</span>
                  </div>
                )}
                {displayMeta && (
                  <div className="flex items-center gap-2 min-w-0 shrink-0">
                    {displayMeta.userId ? (
                      <UserAvatar
                        userId={displayMeta.userId}
                        username={displayMeta.username}
                        className="h-7 w-7 shrink-0"
                      />
                    ) : (
                      <span className="h-7 w-7 shrink-0 rounded-full overflow-hidden bg-wall-border/50 flex items-center justify-center">
                        <span className="text-xs font-medium text-wall-muted leading-none">
                          {displayMeta.username.slice(0, 2).toUpperCase()}
                        </span>
                      </span>
                    )}
                    <span
                      className="text-sm font-medium text-wall-highlight truncate"
                      title={displayMeta.username}
                    >
                      {displayMeta.username}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-wall-muted uppercase">Pos</span>
                  <span className="font-mono text-sm">
                    ({displayPixel.x}, {displayPixel.y})
                  </span>
                </div>
                {displayColor && (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-wall-muted uppercase shrink-0">Color</span>
                    <div
                      className="w-4 h-4 rounded-sm border border-wall-border shrink-0"
                      style={{ backgroundColor: displayColor }}
                      title={displayColor}
                    />
                    <span
                      className="font-mono text-xs text-wall-muted truncate"
                      title={displayColor}
                    >
                      {displayColor}
                    </span>
                  </div>
                )}
                {displayMeta && (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-wall-muted uppercase shrink-0">When</span>
                    <span
                      className="text-sm text-wall-muted truncate"
                      title={
                        displayMeta.placedAt ? new Date(displayMeta.placedAt).toLocaleString() : ''
                      }
                    >
                      {formatPixelTimeAgo(displayMeta.placedAt)}
                    </span>
                  </div>
                )}
                {!displayColor && (
                  <span className="text-sm text-wall-muted italic shrink-0">Empty pixel</span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-[10px] sm:text-sm text-wall-muted">
              <span className="hidden sm:inline">
                Click a pixel to select it, or hover to preview. Use arrow keys to pan.
              </span>
              <span className="sm:hidden">Tap a pixel for details</span>
            </span>
          )}
        </div>
      </div>

      {/* Mobile: Comments toggle button (fixed at bottom; hidden when sheet is open) */}
      {!commentsPanelOpen && (
        <button
          type="button"
          onClick={() => setCommentsPanelOpen(true)}
          className="lg:hidden fixed bottom-4 left-4 right-4 z-20 flex items-center justify-center gap-2 py-3 px-4 bg-wall-surface border border-wall-border/50 rounded-xl shadow-lg text-wall-text font-medium hover:bg-wall-border/30 transition-colors"
          aria-expanded={false}
          aria-label={`Open comments (${comments.length})`}
        >
          <svg
            className="w-5 h-5 text-wall-accent"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          Comments ({comments.length})
        </button>
      )}

      {/* Mobile: Bottom sheet (slides up when open) */}
      <div
        className={`lg:hidden fixed inset-x-0 bottom-0 z-30 flex flex-col bg-wall-surface border-t border-wall-border/50 rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out max-h-[75vh] ${
          commentsPanelOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        aria-hidden={!commentsPanelOpen}
      >
        <div className="shrink-0 px-4 py-3 border-b border-wall-border/50 flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <svg
              className="w-5 h-5 text-wall-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Comments ({comments.length})
          </h2>
          <button
            type="button"
            onClick={() => setCommentsPanelOpen(false)}
            className="p-2 -m-2 text-wall-muted hover:text-wall-text rounded-lg"
            aria-label="Close comments"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {comments.length === 0 ? (
            <p className="text-center text-wall-muted text-sm py-8">
              No comments yet. Be the first to comment!
            </p>
          ) : (
            comments.map((comment) => (
              <ArchiveCommentItem
                key={comment.id}
                comment={comment}
                currentUserId={user?.id}
                currentUserRole={user?.adminRole ?? null}
                deletingCommentId={deletingCommentId}
                votingCommentId={votingCommentId}
                onDelete={handleDeleteClick}
                onVote={handleVote}
              />
            ))
          )}
        </div>
        {user ? (
          <form
            onSubmit={handleSubmitComment}
            className="shrink-0 p-4 border-t border-wall-border/50"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 px-3 py-2 bg-wall-bg border border-wall-border rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent/50"
                maxLength={500}
              />
              <button
                type="submit"
                disabled={!newComment.trim() || isSubmitting}
                className="px-4 py-2 bg-linear-to-r from-wall-accent to-wall-accent-secondary rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isSubmitting ? '...' : 'Post'}
              </button>
            </div>
          </form>
        ) : (
          <div className="shrink-0 p-4 border-t border-wall-border/50 text-center">
            <Link to="/login" className="text-wall-accent hover:underline text-sm">
              Log in to comment
            </Link>
          </div>
        )}
      </div>

      {/* Desktop: Right Comments Panel (always visible on lg+) */}
      <div className="hidden lg:flex w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-wall-border/50 flex-col bg-wall-surface/30 max-h-full">
        <div className="shrink-0 px-4 py-3 border-b border-wall-border/50">
          <h2 className="font-semibold flex items-center gap-2">
            <svg
              className="w-5 h-5 text-wall-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Comments ({comments.length})
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {comments.length === 0 ? (
            <p className="text-center text-wall-muted text-sm py-8">
              No comments yet. Be the first to comment!
            </p>
          ) : (
            comments.map((comment) => (
              <ArchiveCommentItem
                key={comment.id}
                comment={comment}
                currentUserId={user?.id}
                currentUserRole={user?.adminRole ?? null}
                deletingCommentId={deletingCommentId}
                votingCommentId={votingCommentId}
                onDelete={handleDeleteClick}
                onVote={handleVote}
              />
            ))
          )}
        </div>

        {/* Comment Input */}
        {user ? (
          <form
            onSubmit={handleSubmitComment}
            className="shrink-0 p-4 border-t border-wall-border/50"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 px-3 py-2 bg-wall-bg border border-wall-border rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent/50"
                maxLength={500}
              />
              <button
                type="submit"
                disabled={!newComment.trim() || isSubmitting}
                className="px-4 py-2 bg-linear-to-r from-wall-accent to-wall-accent-secondary rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isSubmitting ? '...' : 'Post'}
              </button>
            </div>
          </form>
        ) : (
          <div className="shrink-0 p-4 border-t border-wall-border/50 text-center">
            <Link to="/login" className="text-wall-accent hover:underline text-sm">
              Log in to comment
            </Link>
          </div>
        )}
      </div>

      {/* Delete Comment Confirmation Modal */}
      <ConfirmModal
        isOpen={commentToDelete !== null}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deletingCommentId !== null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
